import { Loader2, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Button } from '../../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Input } from '../../ui/input'

interface BulkDeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  nodeCount: number
  onConfirm: () => void | Promise<void>
  isDeleting?: boolean
}

/**
 * Confirmation dialog for bulk-deleting selected graph nodes.
 * The user must type "delete" to enable the destructive action.
 */
export function BulkDeleteDialog({
  open,
  onOpenChange,
  nodeCount,
  onConfirm,
  isDeleting = false,
}: BulkDeleteDialogProps) {
  const [confirmText, setConfirmText] = useState('')

  const canConfirm = confirmText.toLowerCase() === 'delete' && !isDeleting

  const handleOpenChange = (next: boolean) => {
    if (!next) setConfirmText('')
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete Selected Nodes
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {nodeCount} selected node
            {nodeCount !== 1 ? 's' : ''}? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-2">
            Type <strong className="text-foreground">delete</strong> to confirm.
          </p>
          <Input
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="delete"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter' && canConfirm) {
                onConfirm()
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm()}
            disabled={!canConfirm}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Nodes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
